import { useEffect, useMemo, useState } from "react";
import "./invoice.scss";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { debounce, formatDateFieldsFlat, formatNumberFixed, renderClassNameStatusInvoice } from "../../utils/helper";
import { Button, Col, Flex, Input, Row, Space, Table, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faFilter,
  faPen,
  faSearch,
  faTimeline,
  faPaperclip,
  faCircleXmark,
  faCircleCheck,
  faTriangleExclamation,
  faFileExport,
  faEye,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { IColumnConfig, ISortInfo } from "../../interfaces/common";
import CustomizeTableModal from "../../components/customize-table";
import { ColumnType } from "antd/es/table";
import FilterDrawer from "../../components/filter-drawer";
import { TableViewFilters } from "../../interfaces/table-view";
import TableViewDropdown from "../../components/table-view-dropdown";
import { useTableViews } from "../../hooks/useTableViews";
import dayjs from "dayjs";
import { DATE_FORMAT, DATE_FORMAT_DDMMYYYY_HHMMSS } from "../../constants/common";
import { useUpdateTableView } from "../../hooks/useTableView";
import { countNonEmptyFields, getInvoiceStatuses } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { usePageStore, usePageStoreContext } from "../../context/PageStoreContext";
import { IInvoiceResponse, IInvoiceResult } from "../../interfaces/invoice";
import { InvoiceServices } from "../../services/invoice";
import FilterInvoice from "./components/filter";
import { useDropdownsInvoiceData } from "../../hooks/useDropdownsInvoiceData";
import RejectedModalInvoice from "./components/rejected-modal";
import { InvoiceStatusEnum } from "../../constants/enum";
import CreateAndEditInvoice from "./components/create-and-edit";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";
import { useViewFile } from "../../hooks/useViewFile";
import CreateInvoice from "./components/create";

const COL_FIXED_LEFT = ["supplierCode", "supplierName"];
const TABLE_NAME = "invoices_received";

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = ["invoiceDate"];
  const dateTimeFields = ["receivedAt"];
  const numberFields = ["totalAmount"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }
  if (dateTimeFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : value;
  }
  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value ? formatNumberFixed(value) : value}</div>;
  }

  if (fieldName === "status") {
    return <span className={renderClassNameStatusInvoice(value)}>{value}</span>;
  }

  return value;
};

const setWidthFieldTable = (fieldName: string) => {
  if (["supplierName"].includes(fieldName)) {
    return 300;
  }
  if (["supplierCode", "currency", "status"].includes(fieldName)) {
    return 100;
  }

  if (fieldName === "invoiceNumber") {
    return 150;
  }
  if (fieldName === "receivedAt") {
    return 150;
  }
  return 120;
};

const InvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const [listData, setListData] = useState<IInvoiceResult[]>([]);
  const [statusList, setStatusList] = useState<InvoiceStatusEnum[]>([]);
  const [currentStatus, setCurrentStatus] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const { setPageValue, getPageValue } = usePageStore();
  const { clearAll } = usePageStoreContext();
  const [isModalRejected, setIsModalRejected] = useState(false);
  const [isModalEdit, setIsModalEdit] = useState(false);
  const [isModalCreate, setIsModalCreate] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<IInvoiceResult>();
  const [parentInvoiceId, setParentInvoiceId] = useState<string>();
  const hasInvoiceEditor = useHasPermission([PERMISSIONS.INVOICE_EDITOR]);
  const { loading: loadingFile, onViewFile: onDownloadAttachment } = useViewFile();
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);

  useEffect(() => {
    setSearchValue(getPageValue().searchValue);
    setDebouncedSearchValue(getPageValue().searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    dataTableView,
    setDataTableView,
    loading: loadingTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
  });

  const currentTableView = dataTableView?.currentView!;

  const { filters = {}, columns: columnConfigs = [] } = currentTableView || {};

  const isDefaultView = !!dataTableView?.defaultView;

  const { updateTableView } = useUpdateTableView();

  const fetchStatusByFilter = async (skip?: boolean) => {
    try {
      setLoadingStatus(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        ...formatDateFieldsFlat(filters),
      };
      const response: any = await InvoiceServices.getStatusByFilter(params);
      const status = response?.data || [];
      setStatusList(status);
      if (!skip) {
        setCurrentStatus(getPageValue()?.currentStatus || getInvoiceStatuses(status)?.[0]);
        setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
      } else {
        if (currentStatus && !getInvoiceStatuses(status).includes(currentStatus)) {
          setCurrentStatus(getPageValue()?.currentStatus || getInvoiceStatuses(status)?.[0]);
          setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
        } else {
          fetchData();
        }
      }

      clearAll();
      if (status.length === 0) {
        setListData([]);
      }
    } catch (error) {
      console.error("Fetch Api error:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (!loadingTableView && !currentStatus) {
      fetchStatusByFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTableView, debouncedSearchValue, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = {
        keyword: debouncedSearchValue || undefined,
        ...formatDateFieldsFlat(filters),
        ...(currentStatus && {
          statuses:
            currentStatus === InvoiceStatusEnum.Accepted
              ? [
                  InvoiceStatusEnum.Accepted,
                  InvoiceStatusEnum.Amended,
                  InvoiceStatusEnum.Matched,
                  InvoiceStatusEnum.Exported,
                ].filter((status) => statusList.includes(status))
              : currentStatus,
        }),
        ...(parentInvoiceId && { invoiceId: parentInvoiceId }),
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };
      const response: IInvoiceResponse = await InvoiceServices.getAll(params);
      setListData(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true);
    }
  };

  useEffect(() => {
    if (!loadingTableView && !!currentStatus) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingTableView,
    pagination.current,
    pagination.pageSize,
    sortInfo,
    currentStatus,
    debouncedSearchValue,
    filters,
  ]);

  const { data: dropdownsData } = useDropdownsInvoiceData(isFetchedMaterData);
  const { currencies = [], suppliers = [] } = dropdownsData || {};

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setCurrentStatus(undefined);
        setDebouncedSearchValue(value);
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    setCurrentStatus(undefined);
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
  };

  const columns: ColumnType<IInvoiceResult>[] = useMemo(() => {
    return columnConfigs
      .filter((col) => col.isDisplay && col.column !== "attachmentName")
      .map((col) => {
        return {
          title: col.label,
          dataIndex: col.column,
          key: col.column,
          fixed: COL_FIXED_LEFT.includes(col.column) ? "left" : false,
          sorter: true,
          sortDirections: ["ascend", "descend"],
          render: (value: any) => renderFieldTable(col.column, value),
          ...(setWidthFieldTable(col.column) && { width: setWidthFieldTable(col.column) }),
        };
      });
  }, [columnConfigs]);

  const colActive: ColumnType<IInvoiceResult> = {
    title: "Actions",
    key: "action",
    width: hasInvoiceEditor ? 160 : 120,
    fixed: "right",
    align: "center",
    render: (_: any, record: IInvoiceResult) => {
      const attachment = record.attachments?.[0];
      return (
        <Space size="small" className="box-action" align="center">
          <Tooltip title={record.attachmentName}>
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadAttachment?.(Number(record.id), attachment?.id!, attachment?.fileName!);
              }}
              disabled={!attachment}
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </Button>
          </Tooltip>
          <div>|</div>
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleUpdate(record);
            }}
          >
            {hasInvoiceEditor ? <FontAwesomeIcon icon={faPen} /> : <FontAwesomeIcon icon={faEye} />}
          </Button>
          <div>|</div>
          {hasInvoiceEditor && (
            <>
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRejected(record);
                }}
              >
                <FontAwesomeIcon icon={record.status === InvoiceStatusEnum.Rejected ? faCircleCheck : faCircleXmark} />
              </Button>
              <div>|</div>
            </>
          )}

          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (record.status === InvoiceStatusEnum.Duplicated && record?.duplicate) {
                handleStatus(record.duplicate.status, record.duplicate.id.toString());
              } else {
                linkToAudit(record.id);
              }
            }}
          >
            <FontAwesomeIcon icon={record.status === InvoiceStatusEnum.Duplicated ? faFileExport : faTimeline} />
          </Button>
        </Space>
      );
    },
  };

  const colError: ColumnType<IInvoiceResult> = {
    title: "",
    key: "error",
    width: 40,
    fixed: "left",
    align: "center",
    render: (_: any, record: IInvoiceResult) => <>{contentError(record)}</>,
  };

  const contentError = (record: IInvoiceResult) => {
    const { processErrors } = record;
    if (!processErrors?.length) return null;
    const content = () => (
      <ul style={{ margin: "0", padding: "5px  5px 5px 25px" }}>
        {processErrors.map((item) => (
          <li key={item.field}>{item.error}</li>
        ))}
      </ul>
    );
    return (
      <Tooltip title={content()} placement="right">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
      </Tooltip>
    );
  };

  const linkToAudit = (link: string) => {
    setPageValueInvoice();
    navigate(`/invoice/${link}/audit-trail`);
  };

  const handleUpdate = (invoice: IInvoiceResult) => {
    setIsModalEdit(true);
    setCurrentInvoice(invoice);
  };
  const handleRejected = (invoice: IInvoiceResult) => {
    setIsModalRejected(true);
    setCurrentInvoice(invoice);
  };

  const activeFilterCount = countNonEmptyFields(filters);

  const navigateDetail = (record: IInvoiceResult) => {
    if (record.id) {
      setPageValueInvoice();
      navigate(
        `/supplier-invoice-record?invoice_id=${record.id}&name=${encodeURIComponent(
          record.supplierName ?? ""
        )}&code=${encodeURIComponent(record.supplierCode ?? "")}`
      );
    }
  };

  const handleStatus = (status: InvoiceStatusEnum, parentInvoiceId?: string) => {
    setCurrentStatus(status);
    setParentInvoiceId(parentInvoiceId || undefined);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const setPageValueInvoice = () => {
    setPageValue({
      searchValue,
      pagination,
      currentStatus,
    });
  };

  return (
    <div className="invoice-page">
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <h2 className="title-page">Invoices</h2>
        {hasInvoiceEditor && (
          <Button type="primary" onClick={() => setIsModalCreate(true)}>
            <FontAwesomeIcon icon={faPlus} /> Create
          </Button>
        )}
      </Flex>
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Booking Ref., Supp. Code, Supp. Name or Invoice Number"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 600 }}
          size="large"
          allowClear
        />
        <Flex gap={12}>
          <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <TableViewDropdown
            tableName={TABLE_NAME}
            dataTableView={dataTableView!}
            onTrigger={() => {
              setCurrentStatus(undefined);
              refetchTableViews();
            }}
          />
        </Flex>
      </Flex>
      <Row className="box-status">
        {getInvoiceStatuses(statusList).map((status) => (
          <Col
            span={6}
            key={status}
            className={`${renderClassNameStatusInvoice(status)} ${status === currentStatus ? "active" : ""} item`}
            onClick={() => handleStatus(status)}
          >
            {status}
          </Col>
        ))}
      </Row>
      <Table
        dataSource={listData}
        loading={loading || loadingTableView || loadingStatus || loadingFile}
        rowKey={(record) => record.id}
        columns={[...(currentStatus === InvoiceStatusEnum.Query ? [colError] : []), ...columns, colActive]}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(record, index) => {
          const classes = [];

          if (index % 2 === 0) classes.push("highlight-row");

          return classes.join(" ");
        }}
        scroll={{ x: 1200 }}
        onRow={(record) => ({
          onClick: () => navigateDetail(record),
          style: { cursor: `pointer` },
        })}
      />

      {showModal && (
        <CustomizeTableModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsValue={columnConfigs}
          onSave={handleSaveCustomizeTable}
          tableName={TABLE_NAME}
          isSave={isDefaultView}
        />
      )}
      {openFilter && (
        <FilterDrawer
          title="Filter Invoices"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          values={filters}
          children={<FilterInvoice dropdownsData={dropdownsData} />}
        />
      )}
      {currentInvoice && (
        <RejectedModalInvoice
          isVisible={isModalRejected}
          onClose={(isTrigger) => {
            setIsModalRejected(false);
            if (isTrigger) {
              fetchStatusByFilter(true);
            }
          }}
          invoiceDetail={currentInvoice}
          onGoToEdit={(invoice) => {
            setIsModalRejected(false);
            handleUpdate(invoice);
          }}
        />
      )}

      {currentInvoice?.id && (
        <CreateAndEditInvoice
          isVisible={isModalEdit}
          onClose={(isTrigger) => {
            setIsModalEdit(false);
            if (isTrigger) {
              fetchStatusByFilter(true);
            }
          }}
          id={currentInvoice?.id}
          currencies={currencies}
          suppliers={suppliers}
          isPermissionEdit={hasInvoiceEditor}
        />
      )}

      {isModalCreate && (
        <CreateInvoice
          isVisible={isModalCreate}
          onClose={(isTrigger) => {
            setIsModalCreate(false);
            if (isTrigger) {
              fetchStatusByFilter(true);
            }
          }}
          suppliers={suppliers}
          isPermissionEdit={hasInvoiceEditor}
        />
      )}
    </div>
  );
};

export default InvoicePage;
